import { apiService } from '../services/api';
import {
  buildMediaUploadLimitContext,
  readFileUploadResponse,
  type MediaUploadMediaKind,
  type ResolvedMediaUploadLimits,
} from './mediaUploadLimits';

export type AdminMediaFolder = 'audio' | 'images' | 'videos';

function folderToMediaKind(folder: AdminMediaFolder): MediaUploadMediaKind {
  if (folder === 'images') return 'image';
  if (folder === 'videos') return 'video';
  return 'audio';
}

function blobToFile(blob: Blob, fileName: string): File {
  return new File([blob], fileName, { type: blob.type || 'application/octet-stream' });
}

export function isEmbeddedMediaUrl(url?: string | null): boolean {
  return !!url && (url.startsWith('blob:') || url.startsWith('data:'));
}

export function isServerMediaUrl(url?: string | null): boolean {
  if (!url || isEmbeddedMediaUrl(url)) return false;
  return (
    url.startsWith('/uploads/') ||
    url.startsWith('http://') ||
    url.startsWith('https://')
  );
}

export async function resolvePendingMediaBlob(
  url: string | undefined,
  pendingFile?: File,
  pendingBlob?: Blob
): Promise<Blob | null> {
  if (pendingFile) return pendingFile;
  if (pendingBlob) return pendingBlob;
  if (!url || !isEmbeddedMediaUrl(url)) return null;
  const response = await fetch(url);
  return response.blob();
}

/**
 * Standalone admin multipart upload for quiz/exercise (and any editor without a content-block id).
 * Same storage as lesson `upload-audio-asset` / `upload-sticky-audio` (SaveStandaloneMediaUploadAsync).
 * Lesson blocks with an id still use `/admin/content-blocks/{id}/upload-*` so the server can validate the block.
 */
export async function uploadAdminMediaFile(
  blob: Blob,
  fileName: string,
  folder: AdminMediaFolder,
  limits?: ResolvedMediaUploadLimits
): Promise<string> {
  const file = blob instanceof File ? blob : blobToFile(blob, fileName);
  const formData = new FormData();
  formData.append('file', file);
  const token = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
  const baseURL = apiService.getBaseURL();
  const response = await fetch(`${baseURL}/api/Content/admin/media/${folder}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  const upload = await readFileUploadResponse(
    response,
    limits ? buildMediaUploadLimitContext(limits, folderToMediaKind(folder), file) : undefined
  );
  if (!upload.url) {
    throw new Error(upload.errorMessage || 'Failed to upload media file');
  }
  return upload.url;
}

export async function ensureServerMediaUrl(
  currentUrl: string | undefined,
  folder: AdminMediaFolder,
  fileName: string,
  pendingFile?: File,
  pendingBlob?: Blob,
  limits?: ResolvedMediaUploadLimits
): Promise<string | undefined> {
  if (!currentUrl && !pendingFile && !pendingBlob) return undefined;
  if (currentUrl && isServerMediaUrl(currentUrl) && !pendingFile && !pendingBlob) {
    return currentUrl;
  }

  const blob = await resolvePendingMediaBlob(currentUrl, pendingFile, pendingBlob);
  if (!blob) {
    if (currentUrl && isEmbeddedMediaUrl(currentUrl)) {
      throw new Error('Could not read media for upload. Please re-select the file.');
    }
    return currentUrl;
  }

  return uploadAdminMediaFile(blob, fileName, folder, limits);
}
